import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, CheckCircle, ExternalLink, HelpCircle, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const FOUNDATION_GUIDE = [
  { type: 'Slab-on-Grade', conditions: ['Frost depth < 12"', 'Flat to gentle slope', 'Expansive soil not present', 'No below-grade living'], code: 'IRC R403.1', pros: ['Lowest cost', 'Fast construction', 'Energy efficient'], cons: ['No storage below', 'Plumbing in slab', 'Difficult to modify'] },
  { type: 'Crawlspace', conditions: ['Moderate frost depth', 'Sloped site', 'Need access to utilities', 'Termite-prone area'], code: 'IRC R408', pros: ['Utility access', 'Elevated floor', 'Moderate cost'], cons: ['Moisture management', 'Pest entry points', 'Insulation needed'] },
  { type: 'Basement (Full)', conditions: ['Deep frost line (>36")', 'Usable space desired', 'Well-drained soil', 'Low water table'], code: 'IRC R404', pros: ['Living/storage space', 'Storm shelter', 'Mechanical room'], cons: ['Highest cost', 'Waterproofing critical', 'Egress windows needed'] },
  { type: 'Pier & Beam', conditions: ['Flood zone', 'Steep slope', 'Expansive clay soil', 'Coastal/waterfront'], code: 'IRC R403.1.6', pros: ['Minimal grading', 'Good for problem soils', 'Elevated structure'], cons: ['Floor insulation needed', 'Lateral bracing required', 'Limited access below'] },
];

const WALL_TYPES = [
  { type: 'Bearing Wall', test: 'Supports load from above (floor joists, roof rafters, or other bearing walls)', indicators: ['Runs perpendicular to joists', 'Located at midspan of joists', 'Directly below another wall', 'Under a beam or ridge'], action: 'Cannot be removed without engineering. Header or beam required for openings.' },
  { type: 'Non-Bearing (Partition)', test: 'Divides space only — no structural load from above', indicators: ['Runs parallel to joists', 'Not below another wall or beam', 'Interior closet/bathroom walls'], action: 'Can be relocated or removed without structural modification.' },
  { type: 'Shear Wall', test: 'Resists lateral forces (wind, seismic) per IRC R602.10', indicators: ['Exterior walls', 'Walls with structural sheathing', 'Braced wall lines on plans', 'Required at corners & intervals'], action: 'Cannot be modified without recalculating bracing. Sheathing nailing pattern is critical.' },
];

const HEADER_QUICK_REF = [
  { opening: '3\'-0" or less', singleStory: '2-2x6', twoStory: '2-2x8' },
  { opening: '3\'-0" to 4\'-0"', singleStory: '2-2x6', twoStory: '2-2x10' },
  { opening: '4\'-0" to 5\'-0"', singleStory: '2-2x8', twoStory: '2-2x10' },
  { opening: '5\'-0" to 6\'-0"', singleStory: '2-2x8', twoStory: '2-2x12' },
  { opening: '6\'-0" to 8\'-0"', singleStory: '2-2x10', twoStory: 'Engineered (LVL)' },
  { opening: '8\'-0" to 10\'-0"', singleStory: '2-2x12', twoStory: 'Engineered (LVL)' },
  { opening: '10\'-0" or greater', singleStory: 'Engineered (LVL)', twoStory: 'Engineered — Engineer Required' },
];

export default function StructuralGuide({ darkMode = false, onNavigatePreEng, siteContext = null }) {
  const [expandedFoundation, setExpandedFoundation] = useState(null);
  const [expandedWall, setExpandedWall] = useState(null);
  const [soilType, setSoilType] = useState('');
  const [frostDepth, setFrostDepth] = useState('');
  const [slope, setSlope] = useState('');
  const [recommendation, setRecommendation] = useState(null);
  const [prePopulated, setPrePopulated] = useState(false);

  // Pre-populate from project site conditions
  useEffect(() => {
    if (!siteContext) return;
    let didSet = false;
    if (siteContext.soil_type) {
      const soilMap = {
        'sand': 'well-drained', 'gravel': 'well-drained', 'well-drained': 'well-drained',
        'silt': 'moderate', 'loam': 'moderate', 'moderate': 'moderate',
        'clay': 'expansive-clay', 'expansive': 'expansive-clay', 'expansive-clay': 'expansive-clay',
        'rock': 'rock', 'bedrock': 'rock',
        'flood': 'flood-zone', 'flood-zone': 'flood-zone', 'flood zone': 'flood-zone',
      };
      const mapped = soilMap[siteContext.soil_type.toLowerCase()] || '';
      if (mapped) { setSoilType(mapped); didSet = true; }
    }
    if (siteContext.frost_depth) {
      const fd = String(siteContext.frost_depth).replace(/[^0-9]/g, '');
      if (fd) { setFrostDepth(fd); didSet = true; }
    }
    if (siteContext.slope) {
      const slopeMap = {
        'flat': 'flat', '0-5': 'flat', 'level': 'flat',
        'moderate': 'moderate', '5-15': 'moderate', 'gentle': 'moderate',
        'steep': 'steep', '15+': 'steep', 'hillside': 'steep',
      };
      const mapped = slopeMap[siteContext.slope.toLowerCase()] || '';
      if (mapped) { setSlope(mapped); didSet = true; }
    }
    if (didSet) setPrePopulated(true);
  }, [siteContext]);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : '';

  const analyzeFoundation = () => {
    const frost = parseInt(frostDepth) || 0;
    let rec = 'Slab-on-Grade';
    if (frost > 36 && slope !== 'steep') rec = 'Basement (Full)';
    else if (frost > 12 || slope === 'moderate') rec = 'Crawlspace';
    else if (slope === 'steep' || soilType === 'expansive-clay') rec = 'Pier & Beam';
    if (soilType === 'flood-zone') rec = 'Pier & Beam';
    setRecommendation(rec);
  };

  return (
    <div className={`p-6 space-y-8 ${bg}`} data-testid="structural-guide">
      <div>
        <h2 className={`text-lg font-semibold mb-1 ${text}`}>Structural Guide</h2>
        <p className={`text-sm ${textMuted}`}>Interactive reference tools for residential structural decisions. All final sizing must be verified by a licensed structural engineer.</p>
        {prePopulated && (
          <div className={`flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-[11px] ${darkMode ? 'bg-green-900/15 text-green-300' : 'bg-green-50 text-green-700'}`} data-testid="site-data-indicator">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            Pre-filled from project site conditions
            {siteContext?.address?.city && <span>| {siteContext.address.city}, {siteContext.address.state}</span>}
          </div>
        )}
      </div>

      {/* Foundation Decision Tool */}
      <section>
        <h3 className={`text-sm font-semibold mb-3 ${text}`}>Foundation Type Decision Tool</h3>
        <div className={`${cardBg} border ${border} rounded-xl p-5 space-y-4`}>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={`text-[11px] font-medium ${textMuted}`}>Soil Type</label>
              <Select value={soilType} onValueChange={setSoilType}>
                <SelectTrigger className={`mt-1 h-8 text-xs ${inputCls}`} data-testid="soil-type-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="well-drained">Well-drained (Sand/Gravel)</SelectItem>
                  <SelectItem value="moderate">Moderate (Silt/Loam)</SelectItem>
                  <SelectItem value="expansive-clay">Expansive Clay</SelectItem>
                  <SelectItem value="rock">Rock/Bedrock</SelectItem>
                  <SelectItem value="flood-zone">Flood Zone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`text-[11px] font-medium ${textMuted}`}>Frost Depth (inches)</label>
              <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" placeholder="e.g. 36"
                value={frostDepth} onChange={e => setFrostDepth(e.target.value)} data-testid="frost-depth-input" />
            </div>
            <div>
              <label className={`text-[11px] font-medium ${textMuted}`}>Site Slope</label>
              <Select value={slope} onValueChange={setSlope}>
                <SelectTrigger className={`mt-1 h-8 text-xs ${inputCls}`} data-testid="slope-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat (0-5%)</SelectItem>
                  <SelectItem value="moderate">Moderate (5-15%)</SelectItem>
                  <SelectItem value="steep">Steep (15%+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={analyzeFoundation} className="bg-[#BC5D41] hover:bg-[#70170F] text-white text-xs h-8" data-testid="analyze-foundation-btn">
            Analyze Foundation Type
          </Button>
          {recommendation && (
            <div className={`flex items-start gap-3 p-3 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-50'}`} data-testid="foundation-recommendation">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className={`text-sm font-semibold ${text}`}>Recommended: {recommendation}</div>
                <div className={`text-xs ${textMuted} mt-1`}>
                  {FOUNDATION_GUIDE.find(f => f.type === recommendation)?.code} — Based on your site conditions
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Foundation types accordion */}
        <div className="mt-3 space-y-2">
          {FOUNDATION_GUIDE.map(f => (
            <div key={f.type} className={`${cardBg} border ${border} rounded-lg overflow-hidden`}>
              <button onClick={() => setExpandedFoundation(expandedFoundation === f.type ? null : f.type)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left ${recommendation === f.type ? 'border-l-2 border-l-[#BC5D41]' : ''}`}>
                <span className={`text-xs font-medium ${text}`}>{f.type}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] ${textMuted}`}>{f.code}</span>
                  {expandedFoundation === f.type ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </div>
              </button>
              {expandedFoundation === f.type && (
                <div className={`px-4 pb-3 border-t ${border} pt-3`}>
                  <div className="grid grid-cols-3 gap-4 text-[11px]">
                    <div>
                      <div className={`font-medium mb-1 ${textMuted}`}>Conditions</div>
                      {f.conditions.map(c => <div key={c} className={text}>- {c}</div>)}
                    </div>
                    <div>
                      <div className="font-medium mb-1 text-green-500">Pros</div>
                      {f.pros.map(p => <div key={p} className={text}>+ {p}</div>)}
                    </div>
                    <div>
                      <div className="font-medium mb-1 text-red-400">Cons</div>
                      {f.cons.map(c => <div key={c} className={text}>- {c}</div>)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Wall Type Decision Tree */}
      <section>
        <h3 className={`text-sm font-semibold mb-3 ${text}`}>Wall Type Identification</h3>
        <div className="space-y-2">
          {WALL_TYPES.map(w => (
            <div key={w.type} className={`${cardBg} border ${border} rounded-lg overflow-hidden`}>
              <button onClick={() => setExpandedWall(expandedWall === w.type ? null : w.type)}
                className="w-full flex items-center justify-between px-4 py-3 text-left">
                <span className={`text-xs font-medium ${text}`}>{w.type}</span>
                {expandedWall === w.type ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {expandedWall === w.type && (
                <div className={`px-4 pb-3 border-t ${border} pt-3 text-[11px] space-y-2`}>
                  <div><span className={`font-medium ${textMuted}`}>Test:</span> <span className={text}>{w.test}</span></div>
                  <div>
                    <span className={`font-medium ${textMuted}`}>Indicators:</span>
                    {w.indicators.map(i => <div key={i} className={`${text} ml-3`}>- {i}</div>)}
                  </div>
                  <div className={`flex items-start gap-2 p-2 rounded ${darkMode ? 'bg-amber-900/15' : 'bg-amber-50'}`}>
                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className={text}>{w.action}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Header Quick Reference */}
      <section>
        <h3 className={`text-sm font-semibold mb-3 ${text}`}>Header Quick Reference</h3>
        <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
          <table className="w-full text-xs">
            <thead>
              <tr className={`border-b ${border} ${darkMode ? 'bg-[#222226]' : 'bg-gray-50'}`}>
                <th className={`px-4 py-2.5 text-left font-medium ${textMuted}`}>Opening Width</th>
                <th className={`px-4 py-2.5 text-left font-medium ${textMuted}`}>Single Story</th>
                <th className={`px-4 py-2.5 text-left font-medium ${textMuted}`}>Two Story</th>
              </tr>
            </thead>
            <tbody>
              {HEADER_QUICK_REF.map((row, i) => (
                <tr key={i} className={`border-b last:border-0 ${border}`}>
                  <td className={`px-4 py-2 ${text} font-medium`}>{row.opening}</td>
                  <td className={`px-4 py-2 ${text}`}>{row.singleStory}</td>
                  <td className={`px-4 py-2 ${row.twoStory.includes('Engineer') ? 'text-red-400 font-medium' : text}`}>{row.twoStory}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`px-4 py-2 text-[10px] ${textMuted} flex items-center gap-1`}>
            <HelpCircle className="w-3 h-3" /> Based on #2 SPF, 40 psf live / 10 psf dead load. Per IRC Table R602.7(1). Verify with local requirements.
          </div>
        </div>
      </section>

      {/* Link to Pre-Engineering */}
      {onNavigatePreEng && (
        <div className={`${cardBg} border ${border} rounded-xl p-4 flex items-center justify-between`}>
          <div>
            <div className={`text-xs font-medium ${text}`}>Need detailed calculations?</div>
            <div className={`text-[11px] ${textMuted}`}>Use Pre-Engineering tools for beam sizing, rafter calculations, and more.</div>
          </div>
          <Button variant="outline" size="sm" onClick={onNavigatePreEng} className="text-xs gap-1">
            Pre-Engineering <ExternalLink className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
